import { isObject } from '#object/is-object';

import { isNotDefined } from './is-not-defined.guard';

/**
 * Checks whether the provided value is considered "empty".
 *
 * - For `null` or `undefined`, returns `true`.
 * - For strings, trims whitespace and line breaks, and returns `true` if the result is an empty string.
 * - For arrays, returns `true` if the array has no elements.
 * - For objects, returns `true` if the object has no own enumerable properties.
 * - For all other types, returns `false`.
 *
 * @typeParam T - The type of the value to check.
 * @param value - The value to check for emptiness.
 * @returns `true` if the value is empty according to the above rules, otherwise `false`.
 */
export const isEmpty = <T = unknown>(value: T | null | undefined): boolean => {
  if (isNotDefined(value)) {
    return true;
  }

  if (typeof value === 'string') {
    return (
      value
        .replace(/(\r\n|\n|\r)/gm, ' ')
        .replace(/\s+/g, ' ')
        .trim().length === 0
    );
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (isObject(value)) {
    return Object.keys(value).length === 0;
  }

  return false;
};
